import { Box, List, ListItem, Tooltip, Typography } from '@mui/material'
import React, { useMemo } from 'react'

import { MUIExternalLink } from 'components/common/MUIExternalLink'
import { ReliabilityDonut } from 'components/common/ReliabilityDonut'
import Icon from 'components/Icon'
import {
  DONUT_WIDTH,
  RELIABILITY_REPORT_COLORS
} from 'components/reliability-management/reliability-management-utils'
import { GREMLIN_COM_DOCS_LINKS } from 'constants/external-urls-gremlin-docs'
import { muiTheme } from 'mui-theme'
import theme from 'theme'
import { type PolicyCategory } from 'types/api/policies/policy'
import { sxLinkForDarkBackground } from 'utils/mui-components/link-styling'
import { capitalize } from 'utils/strings'

const COLOR_BOX_WIDTH_HEIGHT = '20px'

const ORDERED_POLICY_CATEGORIES: PolicyCategory[] = [
  'SCALABILITY',
  'REDUNDANCY',
  'DEPENDENCIES',
  'RISKS',
  'OTHER'
]

function getDonutData(policyCategories: PolicyCategory[]) {
  // need to round this to the nearest tenth so that it produces values that add up to 100% for the chart
  const percentage = 100 / policyCategories.length
  const count = Math.round(100 * percentage) / 100

  return policyCategories.map((category) => ({
    gremlin: category,
    count
  }))
}

const ReliabilityGuideDescription: React.FC<{
  fractionOfTotal: string
  policyCategory: PolicyCategory
}> = ({ fractionOfTotal, policyCategory }) => {
  return (
    <ListItem sx={{ paddingY: 0.5 }}>
      <Box
        component="span"
        sx={{
          height: COLOR_BOX_WIDTH_HEIGHT,
          width: COLOR_BOX_WIDTH_HEIGHT,
          backgroundColor: RELIABILITY_REPORT_COLORS[policyCategory],
          marginRight: 1
        }}
      >
        {' '}
      </Box>
      <Typography component="span" variant="body2">
        {fractionOfTotal} of the score will come from the{' '}
        {capitalize(policyCategory)}{' '}
        {policyCategory === 'RISKS' && (
          <Tooltip
            arrow
            placement="right"
            title={
              <>
                <MUIExternalLink
                  href={GREMLIN_COM_DOCS_LINKS.RM_DETECTED_RISKS}
                  sx={sxLinkForDarkBackground}
                >
                  Detected Risks
                </MUIExternalLink>{' '}
                are high-priority reliability concerns that Gremlin
                automatically identified in your environment.
              </>
            }
          >
            <Icon
              css={{
                color: theme.colors.steel[1],
                marginRight: muiTheme.spacing(0.5)
              }}
              icon="solidCircleInfo"
            />
          </Tooltip>
        )}
        category
      </Typography>
    </ListItem>
  )
}

export const ReliabilityGuide: React.FC<{
  policyCategories: Set<PolicyCategory>
  title: string
}> = ({ policyCategories, title }) => {
  const availableCategories: PolicyCategory[] = useMemo(
    () =>
      ORDERED_POLICY_CATEGORIES.filter((category) =>
        policyCategories.has(category)
      ),
    [policyCategories]
  )

  const wrapperId = `test-suite-reliability-donut-${title.replaceAll(' ', '')}`

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Box sx={{ width: DONUT_WIDTH }}>
          <ReliabilityDonut
            data={getDonutData(availableCategories)}
            total={100}
            wrapperId={wrapperId}
          />
        </Box>
        <Box>
          <Typography
            sx={{ fontWeight: 'bold', paddingLeft: 2 }}
            variant="body2"
          >
            {title}
          </Typography>
          <List>
            {availableCategories.map((category) => (
              <ReliabilityGuideDescription
                key={category}
                fractionOfTotal={
                  availableCategories.length === 1
                    ? '100%'
                    : `1/${availableCategories.length}`
                }
                policyCategory={category}
              />
            ))}
          </List>
        </Box>
      </Box>
    </Box>
  )
}
